import type { Flow } from '../../contract/flow';
import type { SyncFlowGate, SyncFlowValue } from '../../contract/sync-read';
import { FlowComputationBase } from '../base/computation';

/**
 * Объект контекста, предоставляемый функциям вычисления потоков.
 */
export interface FlowComputationContext {
    /**
     * Синхронно читает текущее значение из потока.
     *
     * Этот метод устанавливает зависимость между текущим вычислением
     * и предоставленным потоком, гарантируя, что изменения в потоке вызовут пересчет.
     *
     * Асинхронный поток (`AsyncFlow`) сюда передать нельзя — гейт
     * {@link SyncFlowGate} дает ошибку типов. Синхронный computed-поток не умеет ждать
     * данные, поэтому такому потребителю пришлось бы вручную разбирать `pending`/`error`.
     * Вместо этого используйте асинхронный computed-поток с `watchAsync`.
     *
     * В обобщенном хелпере параметр объявляйте как `Flow<Data>`: гейт считается по типу
     * аргумента, и для еще не выведенного типа-параметра его вычислить нельзя.
     *
     * @typeParam TFlow - Тип потока, из которого читается значение
     * @param flow - Поток для чтения значения
     * @returns Текущее значение потока
     * @throws Выбросит ошибку, если поток находится в состоянии ошибки
     *
     * @example
     * ```typescript
     * const value = watch(someFlow); // Читает текущее значение и создает зависимость
     * ```
     */
    readonly watch: <TFlow extends Flow<unknown>>(flow: TFlow & SyncFlowGate<TFlow>) => SyncFlowValue<TFlow>;

    /**
     * Отменяет текущее вычисление и предотвращает обновление значения потока.
     *
     * При вызове этот метод прерывает текущий процесс вычисления. Поток
     * сохранит свое предыдущее значение.
     *
     * @returns Никогда не возвращает значение - всегда выбрасывает исключение для прерывания вычисления
     * @throws Всегда выбрасывает сигнал прерывания для отмены вычисления
     *
     * @example
     * ```typescript
     * if (shouldSkipUpdate) {
     *     skip(); // Вычисление отменено, значение потока не изменилось
     * }
     * ```
     */
    readonly skip: () => never;
}

/**
 * Реализация синхронного вычисления потока.
 *
 * @typeParam T - Тип значения, производимого этим вычислением
 */
export class FlowComputation<T> extends FlowComputationBase<T, FlowComputationContext> {
    /**
     * Создает и возвращает контекст вычисления.
     *
     * @returns Объект контекста
     */
    public getContext(): FlowComputationContext {
        return {
            watch: <TFlow extends Flow<unknown>>(flow: TFlow & SyncFlowGate<TFlow>) =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- гейт разрывает связь TFlow с типом данных потока, восстанавливаем ее вручную
                this.readFlow(flow) as SyncFlowValue<TFlow>,
            skip: () => this.skip(),
        };
    }
}
